import { Building2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';

interface TenantSwitcherProps {
  className?: string;
  /** When true, render as a compact icon button (mobile). */
  compact?: boolean;
}

const ALL_LABEL = 'All FPAs';

export function TenantSwitcher({ className, compact }: TenantSwitcherProps) {
  const {
    tenants,
    selectedTenantId,
    setTenantId,
    currentTenant,
    isLoadingTenants,
    isTenantLocked,
  } = useTenant();
  const label = currentTenant?.name ?? ALL_LABEL;

  // Non-super-admins are scoped to their own tenant — render a read-only
  // chip showing the active FPA so they know the scope, no dropdown.
  if (isTenantLocked) {
    return (
      <Button
        variant="outline"
        size={compact ? 'icon' : 'sm'}
        className={cn(compact ? '' : 'max-w-[180px] gap-2', className)}
        disabled
        title={currentTenant?.name ? `Scoped to ${currentTenant.name}` : 'Scoped to your FPA'}
      >
        <Building2 className="h-4 w-4 shrink-0" />
        {!compact && <span className="truncate">{label}</span>}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={compact ? 'icon' : 'sm'}
          className={cn(compact ? '' : 'max-w-[180px] gap-2', className)}
          disabled={isLoadingTenants}
        >
          <Building2 className="h-4 w-4 shrink-0" />
          {!compact && <span className="truncate">{label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>FPA scope</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setTenantId('')}>
          <Check
            className={cn('mr-2 h-4 w-4', !selectedTenantId ? 'opacity-100' : 'opacity-0')}
          />
          {ALL_LABEL}
        </DropdownMenuItem>
        {tenants.map((tenant) => {
          const id = String(tenant.id);
          const selected = id === selectedTenantId;
          return (
            <DropdownMenuItem key={id} onSelect={() => setTenantId(id)}>
              <Check
                className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')}
              />
              <span className="truncate">{tenant.name}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default TenantSwitcher;
